import type { Rect } from '../core/types';

export interface FloodFillParams {
  /** Straight-alpha RGBA rows, top row first. */
  pixels: Uint8Array;
  width: number;
  height: number;
  startX: number;
  startY: number;
  /** 0..1, compared against the largest per-channel difference. */
  tolerance: number;
  contiguous: boolean;
}

export interface FloodFillResult {
  /** `width * height` bytes: 255 inside the filled region, 0 outside. */
  coverage: Uint8Array;
  bounds: Rect;
}

/**
 * Scanline flood fill. This is the one paint operation that has to run on the
 * CPU, since it is inherently sequential — the layer is read back, filled here,
 * and the resulting coverage mask is uploaded again for the GPU to colour in.
 */
export function floodFill(params: FloodFillParams): FloodFillResult | null {
  const { pixels, width, height, startX, startY, tolerance, contiguous } = params;
  const x0 = Math.floor(startX);
  const y0 = Math.floor(startY);
  if (x0 < 0 || y0 < 0 || x0 >= width || y0 >= height) return null;

  const coverage = new Uint8Array(width * height);
  const limit = Math.round(Math.max(0, Math.min(1, tolerance)) * 255);
  const base = (y0 * width + x0) * 4;
  const seed = [pixels[base], pixels[base + 1], pixels[base + 2], pixels[base + 3]] as const;

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  const matches = (index: number): boolean => {
    const p = index * 4;
    // Fully transparent pixels have meaningless colour, so compare alpha only.
    if (seed[3] === 0) return pixels[p + 3] <= limit;
    return (
      Math.abs(pixels[p] - seed[0]) <= limit &&
      Math.abs(pixels[p + 1] - seed[1]) <= limit &&
      Math.abs(pixels[p + 2] - seed[2]) <= limit &&
      Math.abs(pixels[p + 3] - seed[3]) <= limit
    );
  };

  const mark = (index: number, x: number, y: number): void => {
    coverage[index] = 255;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  };

  if (!contiguous) {
    for (let y = 0; y < height; y += 1) {
      const row = y * width;
      for (let x = 0; x < width; x += 1) {
        if (matches(row + x)) mark(row + x, x, y);
      }
    }
  } else {
    if (!matches(y0 * width + x0)) return null;
    const stack: number[] = [x0, y0];

    while (stack.length > 0) {
      const y = stack.pop()!;
      const x = stack.pop()!;
      const row = y * width;
      if (coverage[row + x] !== 0) continue;

      let left = x;
      while (left > 0 && coverage[row + left - 1] === 0 && matches(row + left - 1)) left -= 1;
      let right = x;
      while (right < width - 1 && coverage[row + right + 1] === 0 && matches(row + right + 1)) {
        right += 1;
      }

      for (let i = left; i <= right; i += 1) mark(row + i, i, y);

      for (const ny of [y - 1, y + 1]) {
        if (ny < 0 || ny >= height) continue;
        const nRow = ny * width;
        for (let i = left; i <= right; i += 1) {
          if (coverage[nRow + i] === 0 && matches(nRow + i)) stack.push(i, ny);
        }
      }
    }
  }

  if (maxX < 0) return null;
  return {
    coverage,
    bounds: { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 },
  };
}
